/**
 * @description
 * Admin route guard component that ensures only admin users
 * can access admin routes. Redirects client users to vscode-lens.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-22
 */

import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../../hooks/useAuth';
import { isDevelopment } from '../../../lib/environment';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Admin route guard component
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect client users to portal vscode-lens
  if (user?.role === 'user' && user?.assignedClientId) {
    return <Navigate to="/portal/vscode-lens" replace />;
  }

  // Allow admin users to proceed
  if (user?.role === 'admin' || user?.isSuperAdmin) {
    return (
      <>
        {children}
        {/* Development mode indicator */}
        {isDevelopment() && (
          <div className="fixed bottom-4 right-4 z-50">
            <div className="rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 border border-yellow-200">
              DEV MODE
            </div>
          </div>
        )}
      </>
    );
  }

  // Fallback: redirect to login for users without proper role
  return <Navigate to="/login" replace />;
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
